import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Text,
  Flex,
  Center,
  IconButton,
  Icon,
  CircularProgress,
  Box,
  Image,
  Card,
  CardBody,
  Skeleton,
  Badge,
  Button,
  Heading,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FaShoppingBasket, FaArrowLeft } from "react-icons/fa";
import { Auth } from "aws-amplify";
import axios from "axios";
import AWS from "aws-sdk";
import { ServiceDrawer } from "../Drawer/ServiceDrawer";
import { useNavigate, useLocation } from "react-router-dom";
import { apiAuth, apiGetAuth } from "../../../Backend/api";
import { propertyLinkPath } from "../../../Helper/propertyLink";
import { guestResumePath } from "../../../Helper/guestStay";

// Configure AWS for S3 image fetching
AWS.config.update({
  region: "us-west-2",
  credentials: new AWS.Credentials(
    process.env.REACT_APP_PUBLIC_KEY,
    process.env.REACT_APP_SECRET_KEY
  ),
});

// ServiceCard component
const ServiceCard = React.memo(({ service, imageUrl, isImageLoaded, hasImageError, onImageLoad, onImageError, onServiceClick, pricePreview }) => {
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const formatPrice = (price) => {
    if (pricePreview) {
      const cur = pricePreview.currency.toUpperCase();
      return `${(pricePreview.total / 100).toLocaleString("en-CA", { style: "currency", currency: cur })} ${cur}`;
    }
    if (!price) return "Free";
    return `$${price}`;
  };

  return (
    <Card
      bg="rgba(255, 255, 255, 0.05)"
      border="1px solid"
      borderColor="rgba(255, 255, 255, 0.1)"
      borderRadius="12px"
      overflow="hidden"
      transition="all 0.3s ease"
      mb={4}
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
        borderColor: 'rgba(129, 126, 255, 0.3)',
      }}
      cursor="pointer"
      onClick={() => onServiceClick(service)}
    >
      {imageUrl && !hasImageError && (
        <Box position="relative" overflow="hidden">
          {!isImageLoaded && (
            <Skeleton h="120px" />
          )}
          <Image
            src={imageUrl}
            alt={service.name}
            w="full"
            h="auto"
            maxH="300px"
            objectFit="cover"
            opacity={isImageLoaded ? 1 : 0}
            transition="opacity 0.3s ease"
            onLoad={() => onImageLoad(service.id)}
            onError={() => onImageError(service.id)}
          />
        </Box>
      )}
      
      <CardBody p={3} pt={4} flex="1" display="flex" flexDirection="column">
        <Flex direction="column" gap={2} flex="1">
          <Text fontSize="14px" fontWeight="700" color="white" lineHeight="1.3" noOfLines={2}>
            {service.name}
          </Text>
      
          <Flex justify="space-between" align="center" mt="auto">
            <Badge colorScheme="purple" variant="subtle" fontSize="9px" px={2} py={1}>
              {service.type}
            </Badge>
            <Text fontSize="14px" fontWeight="700" color="#817EFF">
              {formatPrice(service.price)}
            </Text>
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
});

export const ServicesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { room, booking } = location.state || {};
  
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [imageUrls, setImageUrls] = useState({});
  const [nextPageToken, setNextPageToken] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [priceMap, setPriceMap] = useState({});
  const [priceLoading, setPriceLoading] = useState(false);
  // Host-defined upsell categories — ride along on the by-room response
  // (id + name). Rendered as filter tabs, same pattern as guides catalogs.
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  
  const observerRef = useRef();
  const loadingRef = useRef();

  const formatPrice = (service) => {
    const preview = priceMap[service.id];
    // Hour-tier upsells price from their cheapest duration — "From $X".
    const hasTiers = Array.isArray(service.timeOptions) && service.timeOptions.length > 0;
    const prefix = hasTiers ? "From " : "";
    if (priceLoading) return "Loading...";
    if (preview) {
      const cur = preview.currency.toUpperCase();
      return `${prefix}${(preview.total / 100).toLocaleString("en-CA", { style: "currency", currency: cur })} ${cur}`;
    }
    if (!service.price) return "Free";
    return `${prefix}$${service.price}`;
  };

  // Redirect if no room data
  useEffect(() => {
    if (!room) {
      const propertyPath = propertyLinkPath();
      if (propertyPath) {
        navigate(propertyPath, { replace: true });
        return;
      }
      navigate(guestResumePath(), { replace: true });
    }
  }, [room, navigate]);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleServiceClick = useCallback((service) => {
    setSelectedService(service);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedService(null);
  }, []);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const getNights = () => {
    if (!booking?.checkInDate || !booking?.checkOutDate) return undefined;
    const n = Math.ceil(
      (new Date(booking.checkOutDate) - new Date(booking.checkInDate)) / (1000 * 60 * 60 * 24)
    );
    return n > 0 ? n : undefined;
  };

  const fetchPricePreviews = async (serviceList) => {
    const ids = serviceList.filter((s) => s.price > 0).map((s) => s.id);
    if (ids.length === 0) { setPriceLoading(false); return; }
    setPriceLoading(true);
    try {
      const body = { upsellIds: ids };
      const nights = getNights();
      if (nights) body.nights = nights;
      const res = await apiAuth("/v2/upsells/payment/price-preview", body);
      if (res.success && res.data?.items) {
        const map = {};
        res.data.items.forEach((item) => { map[item.upsellId] = item; });
        setPriceMap((prev) => ({ ...prev, ...map }));
      }
    } catch (err) {
      console.error("ServicesPage - Price preview failed:", err);
    } finally {
      setPriceLoading(false);
    }
  };

  // Memoize the fetchImageUrls function to prevent infinite loops
  const fetchImageUrls = useCallback(async (servicesData) => {
    const newImageUrls = {};
    const cloudfrontBaseUrl = "https://dl6ri8ttf37m3.cloudfront.net";

    for (const service of servicesData) {
        const imageKey = service.images?.[0];
        if (!imageKey) continue;
        
        if (imageKey.startsWith("http")) {
          newImageUrls[service.id] = imageKey;
          continue;
        }

        const optimizedUrl = `${cloudfrontBaseUrl}/public/${encodeURIComponent(imageKey)}?width=600&format=webp`;
        newImageUrls[service.id] = optimizedUrl;
    }

    setImageUrls(prev => ({ ...prev, ...newImageUrls }));
  }, []);

  // Fetch services
  const fetchServices = useCallback(async (pageToken = null, append = false) => {
    if (!room?.id) return;
    
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      // apiGetAuth follows REACT_APP_PAY_API_URL (localhost in dev) — the
      // URL was hardcoded to prod before, which broke local testing.
      let path = `/upsells/by-room/${room.id}?limit=10`;
      if (pageToken) {
        path += `&nextPageToken=${pageToken}`;
      }

      const data = await apiGetAuth(path);

      if (data.success) {
        const activeServices = data.data.upsells.filter(service => service.isActive);

        if (!append) {
          setCategories(data.data.categories || []);
          setSelectedCategory("all");
        }
        
        if (append) {
          setServices(prev => {
            const newServices = [...prev, ...activeServices];
            console.log(`🔧 Loaded more services: ${activeServices.length} new services, total: ${newServices.length}`);
            return newServices;
          });
        } else {
          console.log(`🔧 Initial load: ${activeServices.length} services`);
          setServices(activeServices);
        }
        
        setNextPageToken(data.data.nextPageToken || null);
        setHasMore(!!data.data.nextPageToken);
        
        await fetchImageUrls(activeServices);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load services');
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [room?.id, fetchImageUrls]);

  // Load more services
  const loadMoreServices = useCallback(() => {
    if (hasMore && !loadingMore && nextPageToken) {
      fetchServices(nextPageToken, true);
    }
  }, [hasMore, loadingMore, nextPageToken, fetchServices]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreServices();
        }
      },
      { threshold: 0.1 }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loadMoreServices]);

  useEffect(() => {
    setImageLoadStates({});
    setImageUrls({});
    setNextPageToken(null);
    setHasMore(true);
    fetchServices();
  }, [fetchServices]);

  useEffect(() => {
    if (services.length > 0) {
      fetchPricePreviews(services);
    }
  }, [services]);

  const handleImageLoad = useCallback((serviceId) => {
    setImageLoadStates(prev => ({
      ...prev,
      [serviceId]: true
    }));
  }, []);

  const handleImageError = useCallback((serviceId) => {
    setImageLoadStates(prev => ({
      ...prev,
      [serviceId]: 'error'
    }));
  }, []);

  /* One masonry grid for a list of services — used by both the flat view
   * and each category section. */
  const renderMasonry = (list) => (
    <Flex gap={{ base: 3, md: 5 }}>
      {buildColumns(list).map((column, columnIndex) => (
        <Box key={columnIndex} flex="1" minW={0}>
          {column.map((service) => {
            const hasImage = !!imageUrls[service.id];
            return (
              <Card
                key={service.id}
                direction={"column"}
                overflow="hidden"
                variant="outline"
                bg="rgba(255, 255, 255, 0.05)"
                border="1px solid"
                borderColor="rgba(255, 255, 255, 0.1)"
                borderRadius="12px"
                cursor="pointer"
                transition="all 0.3s ease"
                flex="1"
                minH="180px"
                display="flex"
                mb={4}
                onClick={() => handleServiceClick(service)}
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                  borderColor: 'rgba(129, 126, 255, 0.3)',
                }}
              >
                {hasImage ? (
                  <>
                    <Image
                      objectFit="cover"
                      w="full"
                      src={imageUrls[service.id]}
                      alt={service.name}
                    />
                    <CardBody p={3} pt={4}>
                      <Heading size="md" color="white" fontSize="16px" fontWeight="700" mb={2} textAlign="left">
                        {service.name}
                      </Heading>
                      <Flex justify="space-between" align="center">
                        {priceLoading ? (
                          <Skeleton height="14px" width="60px" />
                        ) : (
                          <Text fontSize="14px" fontWeight="700" color="#817EFF">
                            {formatPrice(service)}
                          </Text>
                        )}
                      </Flex>
                    </CardBody>
                  </>
                ) : (
                  <Flex direction="column" align="flex-start" justify="center" flex="1" minH="180px" px={3} py={4}>
                    <Heading size="md" color="white" fontSize="16px" fontWeight="700" mb={2} noOfLines={1} textAlign="left">
                      {service.name.length > 20 ? `${service.name.substring(0, 20)}...` : service.name}
                    </Heading>
                    {service.description && (
                      <Text fontSize="12px" color="#A0AEC0" mb={2} noOfLines={3} textAlign="left">
                        {service.description}
                      </Text>
                    )}
                    {service.price > 0 && (
                      priceLoading ? (
                        <Skeleton height="14px" width="80px" />
                      ) : (
                        <Text fontSize="14px" color="#817EFF" fontWeight={800} textAlign="left">
                          {formatPrice(service)}
                        </Text>
                      )
                    )}
                  </Flex>
                )}
              </Card>
            );
          })}
        </Box>
      ))}
    </Flex>
  );

  // Pinterest-style masonry layout — 2 columns on phones, more on desktop
  const columnCount = useBreakpointValue({ base: 2, md: 3, xl: 4 }) || 2;

  const buildColumns = useCallback((list) => {
    const columns = Array.from({ length: columnCount }, () => []);
    const columnHeights = Array.from({ length: columnCount }, () => 0);

    list.forEach((service) => {
      const minHeightIndex = columnHeights.indexOf(Math.min(...columnHeights));
      columns[minHeightIndex].push(service);
      const estimatedHeight = 300 + 16;
      columnHeights[minHeightIndex] += estimatedHeight;
    });

    return columns;
  }, [columnCount]);

  /* Category tabs — same pattern as the Guides page catalog tabs: a
   * horizontal pill row that filters the grid. "All" first, then one tab
   * per category that has at least one loaded service, then "Other" for
   * uncategorized services (only when named tabs exist). */
  const categoryTabs = useMemo(() => {
    if (!categories.length) return null;
    const countByCategory = {};
    let uncategorized = 0;
    services.forEach((service) => {
      if (service.categoryID) {
        countByCategory[service.categoryID] = (countByCategory[service.categoryID] || 0) + 1;
      } else {
        uncategorized++;
      }
    });
    const tabs = categories
      .filter((c) => countByCategory[c.id] > 0)
      .map((c) => ({ id: c.id, name: c.name }));
    if (tabs.length === 0) return null;
    if (uncategorized > 0) {
      tabs.push({ id: "__other__", name: "Other" });
    }
    return [{ id: "all", name: "All" }, ...tabs];
  }, [services, categories]);

  const visibleServices = useMemo(() => {
    if (!categoryTabs || selectedCategory === "all") return services;
    if (selectedCategory === "__other__") {
      const known = new Set(categories.map((c) => c.id));
      return services.filter((s) => !s.categoryID || !known.has(s.categoryID));
    }
    return services.filter((s) => s.categoryID === selectedCategory);
  }, [services, categories, categoryTabs, selectedCategory]);

  if (loading) {
    return (
      <Box bg="#1A202C" color="white" minH="100vh" display="flex" flexDirection="column">
        <Box position="sticky" top={0} zIndex={10} bg="#1A202C">
          <Box px={{ base: 3, md: 8 }} pt={4} pb={3} maxW="1100px" mx="auto" w="100%">
            <Flex alignItems="center">
              <IconButton
                icon={<Icon as={FaArrowLeft} />}
                variant="ghost"
                colorScheme="blue"
                bg="#2c3b4b"
                mr={2}
                onClick={handleBack}
                _hover={{ bg: "#34495e" }}
              />
              <Flex alignItems="center" gap={2}>
              <Text fontWeight="bold" fontSize={{ base: "14px", md: "18px" }}>
                  Services
                </Text>
                <FaShoppingBasket />

              </Flex>
            </Flex>
          </Box>
        </Box>
        <Center h="200px">
          <CircularProgress
            isIndeterminate
            color="#504CFF"
            size="30px"
            trackColor="transparent"
          />
        </Center>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg="#1A202C" color="white" minH="100vh" display="flex" flexDirection="column">
        <Box position="sticky" top={0} zIndex={10} bg="#1A202C">
          <Box px={{ base: 3, md: 8 }} pt={4} pb={3} maxW="1100px" mx="auto" w="100%">
            <Flex alignItems="center">
              <IconButton
                icon={<Icon as={FaArrowLeft} />}
                variant="ghost"
                colorScheme="blue"
                bg="#2c3b4b"
                mr={2}
                onClick={handleBack}
                _hover={{ bg: "#34495e" }}
              />
              <Flex alignItems="center" gap={2}>
              <Text fontWeight="bold" fontSize={{ base: "14px", md: "18px" }}>
                  Services
                </Text>
              <FaShoppingBasket />

              </Flex>
            </Flex>
          </Box>
        </Box>
        <Center flex="1" px={4}>
          <Text fontSize="16px" color="gray.400" textAlign="center">
            {error}
          </Text>
        </Center>
      </Box>
    );
  }

  return (
    <Box bg="#1A202C" color="white" minH="100vh" display="flex" flexDirection="column">
      {/* Sticky Header */}
      <Box position="sticky" top={0} zIndex={10} bg="#1A202C">
        <Box px={{ base: 3, md: 8 }} pt={4} pb={3} maxW="1100px" mx="auto" w="100%">
          <Flex alignItems="center">
            <IconButton
              icon={<Icon as={FaArrowLeft} />}
              variant="ghost"
              colorScheme="blue"
              bg="#2c3b4b"
              mr={2}
              onClick={handleBack}
              _hover={{ bg: "#34495e" }}
            />
            <Flex alignItems="center" gap={2}>
            <Text fontWeight="bold" fontSize={{ base: "14px", md: "18px" }}>
                Services
              </Text>
              <FaShoppingBasket />
            </Flex>
          </Flex>
        </Box>
      </Box>

      {/* Content */}
      <Box flex="1" px={{ base: 3, md: 8 }} pb={4} maxW="1100px" mx="auto" w="100%">
        {services.length === 0 ? (
          <Center flex="1" py={8}>
            <Text fontSize="16px" color="gray.400" textAlign="center">
              No services available at the moment.
            </Text>
          </Center>
        ) : (
          <>
            {/* Category tabs — mirrors the Guides page catalog pills. */}
            {categoryTabs && (
              <Flex
                gap={2}
                overflowX="auto"
                pt={3}
                pb={2}
                mb={2}
                sx={{
                  "&::-webkit-scrollbar": { height: "4px" },
                  "&::-webkit-scrollbar-track": {
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "2px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "rgba(129, 126, 255, 0.5)",
                    borderRadius: "2px",
                  },
                }}
              >
                {categoryTabs.map((tab) => (
                  <Button
                    key={tab.id}
                    size="sm"
                    variant={selectedCategory === tab.id ? "solid" : "outline"}
                    bg={selectedCategory === tab.id ? "#817EFF" : "transparent"}
                    color={selectedCategory === tab.id ? "white" : "#817EFF"}
                    borderColor="#817EFF"
                    borderRadius="16px"
                    px={4}
                    py={2}
                    fontSize="12px"
                    fontWeight="600"
                    whiteSpace="nowrap"
                    flexShrink={0}
                    onClick={() => setSelectedCategory(tab.id)}
                    _hover={{
                      bg: selectedCategory === tab.id ? "#615edc" : "rgba(129, 126, 255, 0.1)",
                    }}
                  >
                    {tab.name}
                  </Button>
                ))}
              </Flex>
            )}

            {visibleServices.length === 0 ? (
              <Center py={10}>
                <Text fontSize="14px" color="gray.400">
                  No services in this category.
                </Text>
              </Center>
            ) : (
              renderMasonry(visibleServices)
            )}

            {/* Loading indicator for infinite scroll */}
            {hasMore && (
              <Box ref={loadingRef} py={4} textAlign="center">
                {loadingMore ? (
                  <CircularProgress isIndeterminate color="#504CFF" size="30px" trackColor="transparent" />
                ) : (
                  <Text fontSize="14px" color="gray.400">
                    Scroll to load more
                  </Text>
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Service Drawer */}
      <ServiceDrawer
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        service={selectedService}
        booking={booking}
      />
    </Box>
  );
}; 